import { useState } from "react";
import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import { generateSavedViewUrl } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { usePinView } from "lib/hooks/api/notices/views/usePinView";
import { useUnpinView } from "lib/hooks/api/notices/views/useUnpinView";
import { SavedViewDataTypeUnion } from "lib/types/savedViews";
import { BasicView } from "../SavedViewsMenu";
import ViewAllButton from "../ViewAllButton";

type RecentViewsProps = {
  dataType: SavedViewDataTypeUnion;
  recentViews?: BasicView[];
  pinnedViews: BasicView[];
  setSelectedView: (viewId?: string, viewName?: string, context?: string) => void;
};

function RecentViews({ dataType, recentViews, pinnedViews, setSelectedView }: RecentViewsProps) {
  const { mutate: unpinView, isLoading: isUnpinning } = useUnpinView();
  const { mutate: pinView, isLoading: isPinning } = usePinView();
  const [pinningId, setPinningId] = useState<string | null>(null);
  const [unpinningId, setUnpinningId] = useState<string | null>(null);

  const isPinned = (id: string) => pinnedViews.some((view) => view.id === id);

  const handlePin = (id: string, name: string) => {
    setPinningId(id);

    pinView(
      { id, name },
      {
        onSuccess: () => setPinningId(null),
        onError: () => setPinningId(null),
      },
    );
  };

  const handleUnpin = (id: string, name: string) => {
    setUnpinningId(id);

    unpinView(
      { id, name },
      { onSuccess: () => setUnpinningId(null), onError: () => setUnpinningId(null) },
    );
  };

  return (
    <RecentViewsWrapper>
      <RecentViewsListScroll>
        {recentViews?.slice(0, 6).map((view) => {
          const { id, name } = view;

          return (
            <RecentViewItem key={id}>
              <Button
                as="a"
                buttonType="tertiary"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedView(id, name, "Recent view");
                }}
                href={generateSavedViewUrl(view.id, view.dataType)}
                blackText
                justifyContent="space-between"
                fullWidth
              >
                <Text fontWeight="500">{name}</Text>

                <span className="pinActionIcon">
                  {isPinned(id) ? (
                    <Tooltip>
                      <Button
                        buttonType="tertiary"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleUnpin(id, name);
                        }}
                        icon="pin02"
                        iconProps={{ fill: "currentColor" }}
                        loading={isUnpinning && unpinningId === id}
                        aria-label="Unpin this view"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <Button
                        buttonType="tertiary"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handlePin(id, name);
                        }}
                        icon="pin02"
                        loading={isPinning && pinningId === id}
                        aria-label="Pin this view"
                      />
                    </Tooltip>
                  )}
                </span>
              </Button>
            </RecentViewItem>
          );
        })}
      </RecentViewsListScroll>
      <ViewAllButton filters={{ dataTypes: [dataType] }} fullWidth />
    </RecentViewsWrapper>
  );
}

const RecentViewsWrapper = styled.div({
  display: "flex",
  backgroundColor: "white",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  paddingTop: 8,
  paddingLeft: 8,
});

const RecentViewsListScroll = styled.div({
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const RecentViewItem = styled.div({
  display: "flex",
  alignItems: "center",

  button: {
    padding: "0 8px",
  },

  ":hover": {
    color: "#0052cc",
    cursor: "pointer",

    ".pinActionIcon button": {
      display: "inline-block",
      backgroundColor: "transparent",
    },
  },

  ".pinActionIcon": {
    borderRadius: 4,
    ":hover": {
      backgroundColor: "#1890ff26",
    },
  },
});

export default RecentViews;
